import { useCallback, useEffect, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { isAllowed, type Decision } from "../../../api/access/decisions";
import { projectCheckpointUrl } from "../../../api/content/hostingApi";
import { listReleases } from "../../../api/knowledgeData/releasesApi";
import type { ReleaseSummary } from "../../../api/knowledgeData/releaseDecoders";
import ReleaseButton from "../../../features/knowledgeData/releases/ReleaseButton";
import ReleaseDownloads from "../../../features/knowledgeData/releases/ReleaseDownloads";
import { onReleasesChanged } from "../../../features/knowledgeData/releases/releaseChanges";
import { formatTimeAgo } from "../../../shared/formatting/relativeTimes";
import { useLoad, type LoadHandle } from "../../../shared/loading/useLoad";
import { appPaths } from "../../../shared/routing/appPaths";
import ButtonLink from "../../../ui/buttons/ButtonLink";
import EmptyState from "../../../ui/feedback/EmptyState";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import Section from "../../../ui/forms/Section";

/**
 * Export in the project's settings (F11): the latest release with its download, and
 * "Release…" as on the Data model page; then the whole project, its own Git as one Git
 * bundle (F6; Johannes, 2 Nov: "Download only"; ADR-0042), whose link takes the served
 * decision. Releases are hidden from someone who can't release while there is none (P-B,
 * W-09c); loading and errors still show.
 */
export default function ExportSection({ projectId, releasing, downloading }: { projectId: string; releasing: Decision; downloading: Decision }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listReleases(projectId, signal), [projectId]);
  const releases = useLoad(load);
  const { refresh } = releases;
  useEffect(() => onReleasesChanged(refresh), [refresh]);
  const noReleaseToShow = !isAllowed(releasing) && releases.state.status === "loaded" && releases.state.value.length === 0;
  return (
    <Section title={t("projects.settings.export.title")} hint={t("projects.settings.export.hint")}>
      {noReleaseToShow ? null : (
        <>
          <ExportRow title={t("projects.settings.export.latestRelease")}>
            <ReleaseButton projectId={projectId} decision={releasing} />
          </ExportRow>
          <LatestRelease releases={releases} />
        </>
      )}
      <ExportRow title={t("projects.settings.export.wholeProject")}>
        <ButtonLink href={projectCheckpointUrl(projectId)} download requires={downloading}>
          {t("projects.settings.export.gitBundle")}
        </ButtonLink>
      </ExportRow>
    </Section>
  );
}

function ExportRow({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-2 py-1">
      <h4 className="app-title flex-1 text-sm font-semibold">{title}</h4>
      {children}
    </div>
  );
}

function LatestRelease({ releases }: { releases: LoadHandle<ReleaseSummary[]> }) {
  const { t, i18n } = useTranslation();
  if (releases.state.status !== "loaded") {
    return <LoadStatus state={releases.state} loadingLabel={t("common.loading")} onRetry={releases.reload} compact />;
  }
  // Newest first.
  const latest = releases.state.value[0];
  if (latest === undefined) return <EmptyState size="small">{t("data.release.none")}</EmptyState>;
  return (
    <div className="space-y-1 text-sm">
      <p className="flex flex-wrap items-baseline gap-2">
        <Link to={appPaths.release(latest.datasetId, latest.version.number)} className="app-link font-semibold">
          {t("data.release.numberWithTitle", { number: latest.version.number, title: latest.title })}
        </Link>
        <span className="app-text-muted text-xs">{formatTimeAgo(latest.version.createdAt, i18n.resolvedLanguage)}</span>
      </p>
      <ReleaseDownloads datasetId={latest.datasetId} version={latest.version} />
    </div>
  );
}
